import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  Select,
  Form,
  Popconfirm,
  Input,
  Alert,
  Tag,
} from "antd";

import { contribEnum } from "@seims/shared";
import lookupApi from '../../services/partnershipLookupService';
import { recordError } from "../../utils/logService";

export const ContributeModal = ({
  onShow,
  previewOrgsContrib,
  selectedOrganizations,
  show,
  setOrgsContributions,
  organizationBelongsTo="project",
  onCancel
}) => {

  const isEventsPwa = organizationBelongsTo === "event";
  const [disabled, setDisabled] = useState(true);
  const [contributions, setContributions] = useState([]);
  const [showOther, setShowOther] = useState(false);
  const [selected, setSelected] = useState([]);
  
  const selectedOrganizationsContributions = useMemo(() => {
    if (!selectedOrganizations) return [];
    
    const allContributions = selectedOrganizations
    ?.flatMap(org => org?.contributions ?? [])
    .map(c => c.label);
    
    return allContributions;
  }, [selectedOrganizations]);
  
  useEffect(() => {
    if (!show || !previewOrgsContrib) return;

    // Clear previous partner state
    form.resetFields();
    setShowOther(false);
    setDisabled(true);

    // Always try to load this partner's record
    loadRecord();
    getContributions();
  }, [previewOrgsContrib?.id, show]);

  const { Option } = Select;
  const [form] = Form.useForm();
  const formName = "AddContributions";

  const Confirm = () => {
    form.resetFields();
    onShow(false);
    setDisabled(true);
    setShowOther(false);
  };

  const loadRecord = () => {
    let matchedContributions = [];
    setShowOther(false);

    if (previewOrgsContrib?.contributions !== undefined) {
      matchedContributions = previewOrgsContrib?.contributions || [];
      if (
        previewOrgsContrib?.contributions?.find(
          (c) => c.label == contribEnum.OTHER || c.value == contribEnum.OTHER
        )
      ) {
        setShowOther(true);
      } else {
        setShowOther(false);
      }
    }

    form.setFieldsValue({
      name: previewOrgsContrib.name,
      type: previewOrgsContrib.type,
      contributions: matchedContributions,
      other: previewOrgsContrib.otherContribution,
    });
    setSelected(matchedContributions);
  };

  const getContributions = async () => {
    try {
      const response = await lookupApi.getContributions();
      if (response) {
        const { data } = response;
        const transformData = data.map((c) => ({
          label: c.value,
          value: c.key.toString(),
          key: c.key,
        }));

        setContributions(transformData);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const AssociateContribution = async () => {

    const selectedValues = form.getFieldValue("contributions") || [];

    const contributionItems = selectedValues.map((c) => ({
      label: c.label,
      value: c.value,
    }));

    const otherContribution = form.getFieldValue("other");

    const contibutionsObj = {
      contributions: contributionItems,
      otherContribution: otherContribution,
      id: previewOrgsContrib.id,
      name: previewOrgsContrib.name,
      type:
        typeof previewOrgsContrib.type === "object"
          ? previewOrgsContrib.type.value
          : previewOrgsContrib.type ||
          previewOrgsContrib.partnerType?.value ||
          "",
      typeid: previewOrgsContrib.typeid,
      address: previewOrgsContrib.address,
      email: previewOrgsContrib.email,
      telephone: previewOrgsContrib.telephone,
      newlyAdded: true,
    };

    setOrgsContributions(contibutionsObj);

    form.resetFields();
    setShowOther(false);
    onShow(false);

  };

  const handleCancel = () => {
    onShow(false);
    onCancel?.();
  };

  const onContributeSelection = (selectedValue) => {
    const valuesArray = Array.isArray(selectedValue)
      ? selectedValue
      : selectedValue
        ? [selectedValue]
        : [];

    const hasOther = valuesArray.some(
      (c) => c.label === contribEnum.OTHER
    );

    if (hasOther) {
      setShowOther(true);
    } else {
      setShowOther(false);
      form.setFieldsValue({ other: "" });
    }

    form.validateFields(["contributions"]);
  };


  const validateContributions = async (_, value) => {
    const selectedValues = value || [];

    if (!selectedValues.length) {
      return Promise.reject(new Error("Please select at least one contribution."));
    }

    const hasHostOrg = selectedValues.some(
      (c) => c.label === contribEnum.HOST_ORG
    );

    const hostOrgAlreadySelected = selectedOrganizationsContributions?.some(
      (c) => c === contribEnum.HOST_ORG
    );

    if (hasHostOrg && hostOrgAlreadySelected) {
      return Promise.reject(
        new Error(
          "You have already added a Host Organisation to your event"
        )
      );
    }

    return Promise.resolve();
  }

  return (
      <Modal
        forceRender
        title="Organisation Contributions"
        open={show}
        closable={false}
        keyboard={false}
        maskClosable={false}
        centered
        footer={[
          <div className="modal-footer-buttons">
            <Popconfirm
              key="cancel"
              title="Are you sure you want to cancel...you will lose all info entered?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => Confirm()}
              onCancel={handleCancel}
              disabled={disabled}
            >
              <Button
                onMouseUp={() => handleCancel()}
                style={{ marginLeft: 10 }}
              >
                Cancel
              </Button>
            </Popconfirm>
            <Button
              key="update"
              type="primary"
              htmlType="submit"
              form={formName}
            >
              Update
            </Button>
          </div>,
        ]}
      >
        <p>
          {isEventsPwa ? (<>How does <b>{previewOrgsContrib.name}</b> contribute to your
          event?</>) :
          (<>How does organisation <b>{previewOrgsContrib.name}</b> contribute to your
          project?</>)}
        </p>
        <br />
        <Form
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 14,
          }}
          form={form}
          name={formName}
          onFinish={AssociateContribution}
        >
          <Form.Item
            name="contributions"
            label="Contributions"
            rules={[
              {
                required: true,
                validator: validateContributions,
              },
            ]}
          >
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Please select"
              onChange={onContributeSelection}
              labelInValue
            >
              {contributions.map((contribute) => (
                <Option key={contribute.value} value={contribute.value}>
                  {contribute.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {showOther && (
            <Form.Item
              name="other"
              label="Other Contributions"
              rules={[
                {
                  required: true,
                  message: "Please provide other contributions",
                },
              ]}
            >
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>
  );
};
